import {
  alpha,
  Badge,
  Box,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { styled } from '@mui/material/styles';
import { formatDistance } from 'date-fns';
import { enUS, fr as frLocale } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import {
  markAsRead,
  markAllAsRead,
  removeNotification,
} from '../../../../../redux/notificationsSlice';
import { useNotificationsByKind } from '../../../../../hooks/useNotificationsByKind';
import { useAssistantAccess } from '../../../../../hooks/useAssistantAccess';
import {
  markNotificationAsRead,
  deleteNotification,
} from '../../../../../services/notificationApi';

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

const NotificationItem = styled(ListItemButton, {
  // `read` ne sert qu'au style ci-dessous — sans ce filtre, styled() le
  // transmet tel quel au <button> du DOM, qui n'accepte pas cet attribut
  // (d'où l'avertissement React "non-boolean attribute").
  shouldForwardProp: (prop) => prop !== 'read',
})(
  ({ read }) => `
    background-color: ${read ? 'transparent' : 'rgba(255, 255, 255, 0.03)'};
    padding: 16px 20px;
    transition: all 0.2s ease;
    border: none;
    position: relative;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
      .delete-icon {
        opacity: 1;
      }
    }

    &:not(:last-child) {
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }
`
);

const NotificationDot = styled(Box)(
  ({ theme }) => `
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${theme.palette.primary.main};
    flex-shrink: 0;
`
);

const DeleteIconButton = styled(IconButton)(
  ({ theme }) => `
    position: absolute;
    top: 12px;
    right: 12px;
    opacity: 0;
    transition: opacity 0.2s ease;
    padding: 4px;
    
    &:hover {
      background-color: rgba(244, 67, 54, 0.1);
      color: ${theme.palette.error.main};
    }
`
);

// Regroupe les types de notifications bruts en catégories d'onglets
const getNotificationCategory = (notification) => {
  const type = notification.type || notification.kind;
  if (type === 'company' || type === 'company-status') return 'company';
  if (type === 'suggestion' || type === 'suggestion-accepted') return 'suggestion';
  if (type === 'subscription') return 'subscription';
  if (type === 'payment-proof') return 'payment-proof';
  if (type === 'contact') return 'contact';
  return 'other';
};

const TABS = [
  { value: 'company', labelKey: 'notifications.tabs.company' },
  { value: 'subscription', labelKey: 'notifications.tabs.subscription' },
  { value: 'payment-proof', labelKey: 'notifications.tabs.paymentProof' },
  { value: 'suggestion', labelKey: 'notifications.tabs.suggestion' },
  { value: 'contact', labelKey: 'notifications.tabs.contact' },
];

function HeaderNotifications() {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAssistant, can } = useAssistantAccess();

  // Un assistant ne voit que les onglets de son périmètre (même règle que le
  // backend, qui ne lui renvoie de toute façon rien pour les autres types) :
  // Entreprises toujours, Abonnements/Paiements avec entreprises.validerAbonnement,
  // jamais Suggestions ni Messages.
  const visibleTabs = TABS.filter((tab) => {
    if (!isAssistant) return true;
    if (tab.value === 'subscription' || tab.value === 'payment-proof') {
      return can('entreprises.validerAbonnement');
    }
    if (tab.value === 'suggestion' || tab.value === 'contact') return false;
    return true;
  });

  const { notifications: wsNotifications } = useSelector(
    (state) => state.notifications || { notifications: [], unreadCount: 0 }
  );

  const { notifications: companyNotifications, loading: companyLoading, refetch: refetchCompanies } =
    useNotificationsByKind('company');
  const { notifications: suggestionNotifications, loading: suggestionLoading, refetch: refetchSuggestions } =
    useNotificationsByKind('suggestion');
  const { notifications: subscriptionNotifications, loading: subscriptionLoading, refetch: refetchSubscriptions } =
    useNotificationsByKind('subscription');
  const { notifications: paymentProofNotifications, loading: paymentProofLoading, refetch: refetchPaymentProofs } =
    useNotificationsByKind('payment-proof');
  const { notifications: contactNotifications, loading: contactLoading, refetch: refetchContacts } =
    useNotificationsByKind('contact');

  const allNotifications = [
    ...wsNotifications.map((n) => ({
      ...n,
      source: 'ws',
      isRead: n.read,
      timestamp: new Date(n.createdAt),
      uniqueKey: `ws-${n.id}`,
    })),
    ...companyNotifications.map((n) => ({
      ...n,
      source: 'db',
      type: n.kind,
      isRead: n.view,
      timestamp: new Date(n.createdAt),
      uniqueKey: `db-company-${n._id}`,
    })),
    ...suggestionNotifications.map((n) => ({
      ...n,
      source: 'db',
      type: n.kind,
      isRead: n.view,
      timestamp: new Date(n.createdAt),
      uniqueKey: `db-suggestion-${n._id}`,
    })),
    ...subscriptionNotifications.map((n) => ({
      ...n,
      source: 'db',
      type: n.kind,
      isRead: n.view,
      timestamp: new Date(n.createdAt),
      uniqueKey: `db-subscription-${n._id}`,
    })),
    ...paymentProofNotifications.map((n) => ({
      ...n,
      source: 'db',
      type: n.kind,
      isRead: n.view,
      timestamp: new Date(n.createdAt),
      uniqueKey: `db-payment-proof-${n._id}`,
    })),
    ...contactNotifications.map((n) => ({
      ...n,
      source: 'db',
      type: n.kind,
      isRead: n.view,
      timestamp: new Date(n.createdAt),
      uniqueKey: `db-contact-${n._id}`,
    })),
  ]
    .sort((a, b) => b.timestamp - a.timestamp)
    .filter((notif, index, self) => {
      if (notif.message && notif.message.length > 20) return true;
      return !self.some((other, otherIndex) => {
        if (index === otherIndex) return false;
        const sameType = (notif.type || notif.kind) === (other.type || other.kind);
        const timeDiff = Math.abs(notif.timestamp - other.timestamp);
        return sameType && timeDiff < 60000 && other.message && other.message.length > 20;
      });
    });

  const totalUnread = allNotifications.filter((n) => !n.isRead).length;
  const isLoading = companyLoading || suggestionLoading || subscriptionLoading || paymentProofLoading || contactLoading;

  // Compte les notifications non lues par onglet, pour afficher un badge à côté du libellé
  const unreadCountByTab = useMemo(() => {
    return allNotifications.reduce((acc, n) => {
      if (n.isRead) return acc;
      const category = getNotificationCategory(n);
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
  }, [allNotifications]);

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'all') return allNotifications;
    return allNotifications.filter((n) => getNotificationCategory(n) === activeTab);
  }, [allNotifications, activeTab]);

  const markDbNotificationAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      return true;
    } catch {
      return false;
    }
  };

  const handleOpen = () => {
    setOpen(true);
    refetchCompanies();
    refetchSuggestions();
    refetchSubscriptions();
    refetchPaymentProofs();
    refetchContacts();
  };

  const handleClose = () => setOpen(false);

  const handleNotificationClick = async (notification, event) => {
    if (event?.target?.closest('.delete-icon')) return;

    if (notification.source === 'ws') {
      dispatch(markAsRead(notification.id));
    } else if (notification.source === 'db' && !notification.isRead) {
      const success = await markDbNotificationAsRead(notification._id);
      if (success) {
        const notifType = notification.kind || notification.type;
        if (notifType === 'company') await refetchCompanies();
        else if (notifType === 'suggestion') await refetchSuggestions();
        else if (notifType === 'subscription') await refetchSubscriptions();
        else if (notifType === 'payment-proof') await refetchPaymentProofs();
        else if (notifType === 'contact') await refetchContacts();
      }
    }

    setOpen(false);

    const notifType = notification.type || notification.kind;
    if (notifType === 'company' || notifType === 'company-status') {
      const companyId = notification.data?.id || notification.company;
      if (companyId) navigate(`/management/companies/details/${companyId}`);
    } else if (notifType === 'suggestion' || notifType === 'suggestion-accepted') {
      // ✅ On extrait le nom de la suggestion depuis le message ou data
      // et on le passe via le state de navigation
      const suggestionName =
        notification.data?.name ||        // si le backend envoie data.name
        notification.name ||              // si le champ name existe directement
        notification.message?.match(/["«»""](.+?)["«»""]/)?.[1] || // extrait depuis le message ex: "New suggestion "Food 3" received"
        null;

      navigate('/management/suggestions', {
        state: { searchSuggestion: suggestionName }  // ✅ transmis à SuggestionsTable
      });
    } else if (notifType === 'subscription' || notifType === 'payment-proof') {
      const companyId = notification.data?.companyId || notification.company;
      if (companyId) navigate(`/management/companies/details/${companyId}`);
    } else if (notifType === 'contact') {
      const searchContact =
        notification.data?.email || notification.data?.name || notification.email || null;

      navigate('/management/contact-messages', {
        state: { searchContact }
      });
    }
  };

  const handleDeleteNotification = async (notification, event) => {
    event.stopPropagation();
    if (notification.source === 'ws') {
      dispatch(removeNotification(notification.id));
    } else if (notification.source === 'db') {
      try {
        await deleteNotification(notification._id);
        const notifType = notification.kind || notification.type;
        if (notifType === 'company' || notifType === 'company-status') await refetchCompanies();
        else if (notifType === 'suggestion' || notifType === 'suggestion-accepted') await refetchSuggestions();
        else if (notifType === 'subscription') await refetchSubscriptions();
        else if (notifType === 'payment-proof') await refetchPaymentProofs();
        else if (notifType === 'contact') await refetchContacts();
      } catch {
        // Silencieux : la notification restera visible si la suppression échoue
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    dispatch(markAllAsRead());
    const unreadDbNotifs = allNotifications.filter((n) => n.source === 'db' && !n.isRead);
    for (const notif of unreadDbNotifs) {
      await markDbNotificationAsRead(notif._id);
    }
    refetchCompanies();
    refetchSuggestions();
    refetchSubscriptions();
    refetchPaymentProofs();
    refetchContacts();
  };

  const getNotificationTitle = (notification) => {
    const type = notification.type || notification.kind;
    const titles = {
      suggestion: t('notifications.suggestion'),
      'suggestion-accepted': t('notifications.suggestionAccepted'),
      company: t('notifications.company'),
      'company-status': t('notifications.companyStatus'),
      subscription: t('notifications.subscription'),
      'payment-proof': t('notifications.paymentProof'),
      contact: t('notifications.contact'),
    };
    return titles[type] || t('notifications.title');
  };

  return (
    <>
      <Tooltip arrow title={t('notifications.title')}>
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            badgeContent={totalUnread}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: { xs: 'calc(100vw - 24px)', sm: 420 },
            maxWidth: '100vw',
            maxHeight: { xs: 'calc(100vh - 80px)', sm: 600 },
            backgroundColor: '#1a1d24',
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            borderRadius: 2,
          },
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>
            {t('notifications.title')}
            {totalUnread > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  backgroundColor: 'error.main',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {totalUnread}
              </Box>
            )}
          </Typography>

          {totalUnread > 0 && (
            <Tooltip title={t('notifications.markAllRead')}>
              <IconButton
                size="small"
                onClick={handleMarkAllAsRead}
                sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* TABS */}
        {/* Le composant MUI <Tabs> (défilement puis retour à la ligne) laissait
            certains onglets inatteignables selon la largeur — remplacé par une
            rangée de boutons simples qui passent à la ligne en CSS pur : tous
            les onglets restent toujours visibles, sans mécanisme caché. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            p: 1.25,
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          {visibleTabs.map((tab) => {
            const count = unreadCountByTab[tab.value];
            const totalTabCount =
              tab.value === 'all'
                ? totalUnread
                : count || 0;
            const selected = activeTab === tab.value;
            return (
              <Box
                key={tab.value}
                component="button"
                type="button"
                onClick={() => setActiveTab(tab.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.75,
                  fontSize: '12.5px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  backgroundColor: selected ? 'primary.main' : 'transparent',
                  color: selected ? '#0b1a12' : 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    backgroundColor: selected ? 'primary.main' : 'rgba(255, 255, 255, 0.06)',
                  },
                }}
              >
                <span>{t(tab.labelKey)}</span>
                {totalTabCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      minWidth: 16,
                      height: 16,
                      px: 0.5,
                      borderRadius: '8px',
                      backgroundColor: selected ? 'rgba(11, 26, 18, 0.25)' : 'error.main',
                      color: selected ? '#0b1a12' : '#fff',
                      fontSize: '10px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {totalTabCount}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* LIST */}
        <List sx={{ p: 0, overflow: 'auto', maxHeight: 480 }}>
          {isLoading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                {t('notifications.loading')}
              </Typography>
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                {t('notifications.noNotifications')}
              </Typography>
            </Box>
          ) : (
            filteredNotifications.map((notification, index) => (
              <NotificationItem
                key={notification.uniqueKey || index}
                read={notification.isRead}
                onClick={(e) => handleNotificationClick(notification, e)}
              >
                <Box display="flex" alignItems="flex-start" gap={1.5} width="100%" pr={4}>
                  {!notification.isRead && <NotificationDot />}

                  <Box flex="1" minWidth={0}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: notification.isRead ? 400 : 600,
                          color: notification.isRead ? 'rgba(255, 255, 255, 0.6)' : '#fff',
                        }}
                      >
                        {getNotificationTitle(notification)}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          color: '#ffffff',
                          whiteSpace: 'nowrap',
                          textTransform: 'uppercase',
                        }}
                      >
                        {formatDistance(notification.timestamp, new Date(), {
                          addSuffix: true,
                          locale: i18n.language?.startsWith('fr') ? frLocale : enUS,
                        })}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '13px',
                        color: '#ffffff',
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                      }}
                    >
                      {notification.message}
                    </Typography>
                  </Box>
                </Box>

                <DeleteIconButton
                  className="delete-icon"
                  size="small"
                  onClick={(e) => handleDeleteNotification(notification, e)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </DeleteIconButton>
              </NotificationItem>
            ))
          )}
        </List>
      </Popover>
    </>
  );
}

export default HeaderNotifications;